import {
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import type { PedidosLojaFilters } from "hooks/usePedidosLoja";
import { PEDIDOS_ACCENT, pedidosToolbarSx } from "./pedidos.theme";

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Todos" },
  { value: "AGUARDANDO_PAGAMENTO", label: "Aguard. pag." },
  { value: "PAGO", label: "Pago" },
  { value: "EM_PROCESSAMENTO", label: "Em processamento" },
  { value: "PROCESSANDO", label: "Processando (legado)" },
  { value: "ENVIADO", label: "Enviado" },
  { value: "ENTREGUE", label: "Entregue" },
  { value: "CANCELADO", label: "Cancelado" },
  { value: "DEVOLUCAO_SOLICITADA", label: "Devolução" },
  { value: "DEVOLVIDO", label: "Devolvido" },
];

const PAGE_SIZE_OPTIONS = [10, 20, 25, 50, 100];

type PedidosFiltersProps = {
  filters: PedidosLojaFilters;
  pageSize: number;
  total: number;
  onQueryChange: (value: string) => void;
  onStableFilterChange: (
    field: "status" | "dataInicio" | "dataFim",
    value: string
  ) => void;
  onPageSizeChange: (size: number) => void;
  onPesquisar: () => void;
};

export default function PedidosFilters({
  filters,
  pageSize,
  total,
  onQueryChange,
  onStableFilterChange,
  onPageSizeChange,
  onPesquisar,
}: PedidosFiltersProps) {
  return (
    <MDBox sx={pedidosToolbarSx}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <ShoppingBagOutlinedIcon sx={{ color: PEDIDOS_ACCENT }} />
          <MDTypography variant="button" fontWeight="medium" color="text">
            {total > 0
              ? `${total} pedido${total === 1 ? "" : "s"} encontrado${total === 1 ? "" : "s"}`
              : "Nenhum pedido no filtro atual"}
          </MDTypography>
        </Stack>

        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", lg: "flex-end" }}
        >
          <FormControl variant="outlined" size="small" sx={{ minWidth: { lg: 280 }, flex: 1 }}>
            <InputLabel htmlFor="pedidos-search">Buscar pedido</InputLabel>
            <OutlinedInput
              id="pedidos-search"
              value={filters.query}
              onChange={(e) => onQueryChange(e.target.value)}
              label="Buscar pedido"
              placeholder="Comprador, nº pedido, ID, rastreio…"
              endAdornment={
                <InputAdornment position="end">
                  <Search fontSize="small" />
                </InputAdornment>
              }
              sx={{ bgcolor: "background.paper", borderRadius: 1.5 }}
            />
          </FormControl>

          <FormControl variant="outlined" size="small" sx={{ minWidth: 160 }}>
            <InputLabel shrink htmlFor="pedidos-data-inicio">
              Criação (início)
            </InputLabel>
            <OutlinedInput
              id="pedidos-data-inicio"
              type="date"
              value={filters.dataInicio}
              onChange={(e) => onStableFilterChange("dataInicio", e.target.value)}
              label="Criação (início)"
              notched
              sx={{ bgcolor: "background.paper", borderRadius: 1.5 }}
            />
          </FormControl>

          <FormControl variant="outlined" size="small" sx={{ minWidth: 160 }}>
            <InputLabel shrink htmlFor="pedidos-data-fim">
              Criação (fim)
            </InputLabel>
            <OutlinedInput
              id="pedidos-data-fim"
              type="date"
              value={filters.dataFim}
              onChange={(e) => onStableFilterChange("dataFim", e.target.value)}
              label="Criação (fim)"
              notched
              sx={{ bgcolor: "background.paper", borderRadius: 1.5 }}
            />
          </FormControl>

          <MDButton
            variant="outlined"
            color="dark"
            size="small"
            onClick={onPesquisar}
            sx={{ minWidth: 44, px: 1.5, alignSelf: { xs: "flex-start", lg: "center" } }}
            aria-label="Atualizar listagem"
          >
            <RefreshOutlinedIcon fontSize="small" />
          </MDButton>
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          <ToggleButtonGroup
            exclusive
            size="small"
            value={filters.status}
            onChange={(_, next: string | null) => {
              if (next === null) return;
              onStableFilterChange("status", next);
            }}
            sx={{
              flexWrap: "wrap",
              "& .MuiToggleButton-root": {
                px: 1.25,
                py: 0.75,
                textTransform: "none",
                fontSize: "0.8125rem",
                "&.Mui-selected": {
                  bgcolor: "rgba(249, 96, 48, 0.1)",
                  borderColor: PEDIDOS_ACCENT,
                  color: PEDIDOS_ACCENT,
                  fontWeight: 600,
                },
              },
            }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <ToggleButton key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Stack direction="row" alignItems="center" spacing={1} flexShrink={0}>
            <Typography variant="caption" color="text.secondary">
              Por página
            </Typography>
            <Select
              size="small"
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              sx={{ minWidth: 72, bgcolor: "background.paper", borderRadius: 1.5 }}
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        </Stack>
      </Stack>
    </MDBox>
  );
}
